import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Form, Input, Select, Button, message } from 'antd';

const { TextArea } = Input;

const encodePath = (path) => {
  if (!path) return "";
  return path.split('/').map(encodeURIComponent).join('/');
};

const MetadataEditModal = ({ open, file, onCancel, onUpdate, theme, api }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState({ tags: [], categories: [] });
  const [originalFileName, setOriginalFileName] = useState('');

  const loadMetadata = useCallback(async () => {
    if (!file) return;
    try {
      // 提取不带扩展名的文件名
      const ext = file.filename.includes(".")
        ? file.filename.substring(file.filename.lastIndexOf("."))
        : "";
      const base = ext ? file.filename.slice(0, -ext.length) : file.filename;
      setOriginalFileName(base);
      
      const response = await api.get(`/images/meta/${encodePath(file.relPath)}`);
      if (response.data && response.data.success) {
        const metadata = response.data.data;
        form.setFieldsValue({
          fileName: metadata.title || base,
          description: metadata.description || '',
          tags: metadata.tags || [],
          category: Array.isArray(metadata.category) ? metadata.category : (metadata.category ? [metadata.category] : []),
          characterName: metadata.characterName || '',
          style: metadata.style || ''
        });
      } else {
        form.setFieldsValue({
          fileName: base
        });
      }
    } catch (error) {
      console.error('Error loading metadata:', error);
      message.error('加载元数据失败');
    }
  }, [file, api, form]);

  const loadOptions = useCallback(async () => {
    try {
      const response = await api.get('/tags-categories');
      if (response.data && response.data.success) {
        setOptions({
          tags: response.data.data.tags || [],
          categories: response.data.data.categories || []
        });
      }
    } catch (error) {
      console.error('Error loading options:', error);
    }
  }, [api]);

  useEffect(() => {
    if (open && file) {
      loadMetadata();
      loadOptions();
    }
  }, [open, file, loadMetadata, loadOptions]);

  const handleSave = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      
      let updatedFile = { ...file };
      
      // 检查文件名是否修改
      if (values.fileName !== originalFileName && file) {
        // 提取原始文件扩展名
        const ext = file.filename.includes(".")
          ? file.filename.substring(file.filename.lastIndexOf("."))
          : "";
        const newNameRaw = values.fileName.trim();
        const hasExt = /\.[A-Za-z0-9]+$/.test(newNameRaw);
        const newName = hasExt ? newNameRaw : `${newNameRaw}${ext}`;
        
        // 保存文件名（修改实际文件名称）
        const renameResponse = await api.put(`/images/${encodePath(file.relPath)}`, {
          newName,
        });
        
        if (renameResponse.data && renameResponse.data.success) {
          message.success('文件名修改成功');
          updatedFile = renameResponse.data.data;
        } else {
          message.error('文件名修改失败');
          return;
        }
      }
      
      // 准备元数据，将fileName作为title
      const metadata = {
        ...values,
        title: values.fileName
      };
      delete metadata.fileName;
      
      // 保存元数据
      await api.put(`/images/metadata/${encodePath(updatedFile.relPath)}`, metadata);
      
      message.success('元数据保存成功');
      onUpdate(updatedFile);
      onCancel();
    } catch (error) {
      console.error('Error saving:', error);
      message.error('保存失败');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      // 清空所有元数据字段
      const emptyMetadata = {
        title: originalFileName,
        description: '',
        tags: [],
        category: [],
        characterName: '',
        style: ''
      };
      await api.put(`/images/metadata/${encodePath(file.relPath)}`, emptyMetadata);
      message.success('元数据删除成功');
      form.setFieldsValue({
        fileName: originalFileName,
        description: '',
        tags: [],
        category: [],
        characterName: '',
        style: ''
      });
      onUpdate();
    } catch (error) {
      console.error('Error deleting metadata:', error);
      message.error('删除元数据失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="编辑元数据"
      open={open}
      onCancel={onCancel}
      footer={[
        <Button key="delete" danger onClick={handleDelete} loading={loading}>
          删除元数据
        </Button>,
        <Button key="cancel" onClick={onCancel} disabled={loading}>
          取消
        </Button>,
        <Button key="save" type="primary" onClick={handleSave} loading={loading}>
          保存
        </Button>
      ]}
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        style={{
          color: theme === 'dark' ? '#fff' : '#000'
        }}
      >
        <Form.Item
          name="fileName"
          label="文件名"
        >
          <Input placeholder="请输入文件名" />
        </Form.Item>
        
        <Form.Item
          name="characterName"
          label="角色名"
        >
          <Input placeholder="请输入角色名" />
        </Form.Item>
        
        <Form.Item
          name="description"
          label="描述"
        >
          <TextArea rows={4} placeholder="请输入描述" />
        </Form.Item>
        
        <Form.Item
          name="category"
          label="分类"
        >
          <Select
            mode="multiple"
            placeholder="请选择分类"
            style={{ width: '100%' }}
            options={options.categories.map(category => ({ value: category, label: category }))}
          />
        </Form.Item>
        
        <Form.Item
          name="style"
          label="风格"
        >
          <Input placeholder="请输入风格" />
        </Form.Item>
        
        <Form.Item
          name="tags"
          label="标签"
        >
          <Select
            mode="multiple"
            placeholder="请选择标签"
            style={{ width: '100%' }}
            options={options.tags.map(tag => ({ value: tag, label: tag }))}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default MetadataEditModal;